"use client";

import React, { useState, useMemo } from "react";
import { Search, MapPin, ChevronRight, Star } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { provinces } from "./data/provinces";
import {
  getAttractionsByProvince,
  searchAttractions,
} from "./data/attractions";

const ProvincesPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Calculate attraction counts for each province
  const provincesWithCounts = useMemo(() => {
    return provinces.map((province) => ({
      ...province,
      attractionCount: getAttractionsByProvince(province.id).length,
    }));
  }, []);

  // Filter provinces based on search
  const filteredProvinces = useMemo(() => {
    if (!searchTerm) return provincesWithCounts;

    const lowerSearch = searchTerm.toLowerCase();
    return provincesWithCounts.filter(
      (province) =>
        province.name.toLowerCase().includes(lowerSearch) ||
        province.description.toLowerCase().includes(lowerSearch) ||
        province.districts.some((d) => d.toLowerCase().includes(lowerSearch)) ||
        getAttractionsByProvince(province.id).some(
          (attraction) =>
            attraction.title.toLowerCase().includes(lowerSearch) ||
            attraction.town.toLowerCase().includes(lowerSearch) ||
            attraction.district.toLowerCase().includes(lowerSearch) ||
            attraction.shortDescription.toLowerCase().includes(lowerSearch) ||
            attraction.category.toLowerCase().includes(lowerSearch),
        ),
    );
  }, [searchTerm, provincesWithCounts]);

  // Search attractions directly
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    return searchAttractions(searchTerm.trim());
  }, [searchTerm]);

  const isSearching = searchTerm.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-36 pb-16">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Lily Lanka Destination Review
          </h1>
          <p className="text-lg sm:text-xl text-gray-900 max-w-2xl mx-auto">
            Explore Sri Lanka's provinces and discover amazing attractions
          </p>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search provinces, districts, cities, attractions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                maxLength={50}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-colors">
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isSearching ? (
          // ── ATTRACTION SEARCH RESULTS ──────────────────────────────────────
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Search Results
              </h2>
              <p className="text-gray-600">
                {searchResults.length === 0
                  ? `No attractions found for "${searchTerm}"`
                  : `Found ${searchResults.length} attraction${searchResults.length !== 1 ? "s" : ""} for "${searchTerm}"`}
              </p>
            </div>

            {searchResults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((attraction) => (
                  <Link
                    key={attraction.id}
                    href={`/travel-guides/${attraction.provinceId}/${attraction.id}`}
                    className="group"
                  >
                    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden transform group-hover:-translate-y-1">
                      {/* Image */}
                      <div className="relative h-56 overflow-hidden">
                        <Image
                          src={attraction.image}
                          alt={attraction.title}
                          fill
                          className="object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-xl font-bold text-white mb-1 line-clamp-2">
                            {attraction.title}
                          </h3>
                          <p className="text-purple-200 text-sm flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {attraction.town}, {attraction.district}
                          </p>
                        </div>
                        {/* Category badge */}
                        <div className="absolute top-3 right-3">
                          <span className="bg-purple-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                            {attraction.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {attraction.shortDescription}
                        </p>

                        {/* Rating */}
                        <div className="mb-4 flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium text-gray-700">
                            {attraction.rating}
                          </span>
                          <span className="text-sm text-gray-400">
                            ({attraction.reviews.toLocaleString()} reviews)
                          </span>
                        </div>

                        {/* Explore Button */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="text-sm text-gray-500">
                            {attraction.province}
                          </span>
                          <div className="flex items-center text-purple-600 font-medium text-sm">
                            View Details
                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  <Search className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No attractions found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try a different search term
                </p>
                <button
                  onClick={() => setSearchTerm("")}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Clear Search
                </button>
              </div>
            )}
          </>
        ) : (
          // ── DEFAULT: PROVINCE CARDS ────────────────────────────────────────
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Explore by Province
              </h2>
              <p className="text-gray-600">
                Showing {filteredProvinces.length} of {provinces.length}{" "}
                provinces
              </p>
            </div>

            {filteredProvinces.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProvinces.map((province) => (
                  <Link
                    key={province.id}
                    href={`/travel-guides/${province.id}`}
                    className="group"
                  >
                    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden transform group-hover:-translate-y-1">
                      {/* Image */}
                      <div className="relative h-56 overflow-hidden">
                        <Image
                          src={province.image}
                          alt={province.name}
                          fill
                          className="object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-2xl font-bold text-white mb-1">
                            {province.name}
                          </h3>
                          <p className="text-purple-200 text-sm flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {province.districts.length} Districts •{" "}
                            {province.attractionCount} Attractions
                          </p>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {province.description}
                        </p>

                        {/* Districts */}
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-2 font-medium">
                            DISTRICTS:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {province.districts.slice(0, 3).map((district) => (
                              <span
                                key={district}
                                className="text-xs bg-purple-50 dark:bg-gray-900 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full"
                              >
                                {district}
                              </span>
                            ))}
                            {province.districts.length > 3 && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                +{province.districts.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Explore Button */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="text-sm text-gray-500">
                            {province.attractionCount} places to visit
                          </span>
                          <div className="flex items-center text-purple-600 font-medium text-sm">
                            Explore
                            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  <Search className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No provinces found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your search criteria
                </p>
                <button
                  onClick={() => setSearchTerm("")}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Clear Search
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProvincesPage;
